import { javaIsBlank, javaRound, javaStrip } from '../javaText.ts';

/**
 * The facts the Consistency Check reads from an entry's free-form fields, as the desktop's
 * `consistency.WorldFacts`: each fact is a set of recognised key spellings ("born",
 * "Birth Year" and "date_of_birth" all mean the same, compared without case, spaces or
 * punctuation) and a forgiving value reader ("Year 402", "30 BCE", "-18", "2,400,000",
 * "45k", "about 1.2 million").
 */

/** The fields a check reads, in the entry's order. */
export type Fields = readonly (readonly [string, string])[];

export function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

interface DateFactInfo {
  readonly verb: string;
  readonly noun: string;
  readonly keys: readonly string[];
}

const dateFact = (verb: string, noun: string, ...keys: string[]): DateFactInfo => ({ verb, noun, keys: keys.map(normalizeKey) });

export const DATE_FACTS = {
  BIRTH: dateFact('born', 'birth', 'born', 'birth', 'birthyear', 'yearofbirth', 'dateofbirth', 'dob', 'birthdate'),
  DEATH: dateFact('died', 'death', 'died', 'death', 'deathyear', 'yearofdeath', 'dateofdeath', 'deathdate', 'dod'),
  FOUNDED: dateFact('founded', 'founding', 'founded', 'founding', 'foundedin', 'established', 'founded year', 'built', 'created'),
  ENDED: dateFact('ended', 'end', 'destroyed', 'fell', 'fallen', 'abandoned', 'dissolved', 'ended', 'disbanded', 'ruined'),
} as const;
export type DateFact = keyof typeof DATE_FACTS;

export const QUANTITY_FACTS = {
  POPULATION: { label: 'population', keys: ['population', 'pop', 'inhabitants', 'residents', 'citizens', 'headcount'].map(normalizeKey) },
  /** How many people the place's own food can feed. */
  FOOD: { label: 'food production', keys: ['foodproduction', 'foodsupply', 'food', 'peoplefed', 'feeds', 'foodoutput', 'harvestfeeds'].map(normalizeKey) },
} as const;
export type QuantityFact = keyof typeof QUANTITY_FACTS;

/** A year read from somewhere, with the text it came from, for messages. */
export interface Dated {
  readonly year: number;
  readonly display: string;
}

/** A recognised fact whose value couldn't be read. */
export interface Unreadable {
  readonly key: string;
  readonly value: string;
  readonly expected: string;
}

const S = '[ \\t\\n\\x0B\\f\\r]';
const YEAR_WORD = new RegExp(`\\b(?:year|yr)\\.?${S}*(-?\\d[\\d,]*)${S}*([a-z.]*)`, 'i');
const BARE_YEAR = new RegExp(`^${S}*(-?\\d[\\d,]*)${S}*([a-z. ]*)$`, 'i');
const QUANTITY = new RegExp(`(-?\\d[\\d,]*(?:\\.\\d+)?)${S}*(k|thousand|m|mil|million|mn|b|bn|billion)?\\b`, 'i');

/** The key and value of the first recognised spelling present with a value, if any. */
export function rawFact(fields: Fields, keys: readonly string[]): readonly [string, string] | null {
  for (const [key, value] of fields) {
    if (keys.includes(normalizeKey(key)) && !javaIsBlank(value)) return [key, javaStrip(value)];
  }
  return null;
}

export function dateOf(fields: Fields, fact: DateFact): Dated | null {
  const field = rawFact(fields, DATE_FACTS[fact].keys);
  if (!field) return null;
  const year = parseYear(field[1]);
  return year === null ? null : { year, display: field[1] };
}

export function quantityOf(fields: Fields, fact: QuantityFact): number | null {
  const field = rawFact(fields, QUANTITY_FACTS[fact].keys);
  return field ? parseQuantity(field[1]) : null;
}

/** Every recognised fact on the entry whose value couldn't be read. */
export function unreadable(fields: Fields): Unreadable[] {
  const problems: Unreadable[] = [];
  for (const fact of Object.values(DATE_FACTS)) {
    const field = rawFact(fields, fact.keys);
    if (field && parseYear(field[1]) === null) problems.push({ key: field[0], value: field[1], expected: 'a year, like "402", "Year 402", or "30 BCE"' });
  }
  for (const fact of Object.values(QUANTITY_FACTS)) {
    const field = rawFact(fields, fact.keys);
    if (field && parseQuantity(field[1]) === null) problems.push({ key: field[0], value: field[1], expected: 'a number, like "45000", "45,000", or "45k"' });
  }
  return problems;
}

/** A year from free text: "402", "-18", "Year 402", "Highsun 3, Year 17", "402 AE", "30 BCE" (negative). */
export function parseYear(text: string): number | null {
  if (javaIsBlank(text)) return null;
  const word = YEAR_WORD.exec(text);
  if (word) return signed(word[1]!, word[2] ?? '');
  const bare = BARE_YEAR.exec(text);
  if (bare) return signed(bare[1]!, bare[2] ?? '');
  return null;
}

function signed(number: string, suffix: string): number | null {
  const value = Number(number.replaceAll(',', ''));
  if (!Number.isFinite(value)) return null;
  const era = suffix.toLowerCase().replace(/[^a-z]/g, '');
  const before = era === 'bce' || era === 'bc' || era.startsWith('before');
  return before ? -Math.abs(value) : value;
}

/** A count from free text: "2400000", "2,400,000", "45k", "1.2 million", "about 3000 souls". */
export function parseQuantity(text: string): number | null {
  if (javaIsBlank(text)) return null;
  const match = QUANTITY.exec(text);
  if (!match) return null;
  const value = Number(match[1]!.replaceAll(',', ''));
  if (!Number.isFinite(value)) return null;
  const unit = (match[2] ?? '').toLowerCase();
  const multiplier = unit === 'k' || unit === 'thousand' ? 1_000 : ['m', 'mil', 'million', 'mn'].includes(unit) ? 1_000_000 : ['b', 'bn', 'billion'].includes(unit) ? 1_000_000_000 : 1;
  return value * multiplier;
}

/** "6 years", "1 year", "less than a year": a gap between dates, for messages. */
export function years(amount: number): string {
  const rounded = javaRound(Math.abs(amount) * 10) / 10;
  if (rounded < 1) return 'less than a year';
  const number = rounded === Math.round(rounded) ? String(Math.trunc(rounded)) : rounded.toFixed(1);
  return `${number}${rounded === 1 ? ' year' : ' years'}`;
}

/** "2,400,000": a count, for messages (`String.format(Locale.ROOT, "%,d", Math.round(value))`). */
export function count(value: number): string {
  return BigInt(javaRound(value)).toLocaleString('en-US');
}
